import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowRight, Check, ChevronDown, ChevronRight, Globe2, Menu, X, Home, Sparkles, CreditCard, KeyRound, ListVideo, Users, PanelLeftClose, type LucideIcon } from "lucide-react";
import { useLanguage } from "@/components/providers/language-provider";
import { t, type Lang } from "@/utils/i18n";
import { tHome } from "@/utils/i18n.home";
import { getHomeCopy } from "./copy";

/** Shared header and footer for the home and marketing pages. */
export const HOME = "/";
const LANGS: Lang[] = ["pt", "en", "es", "de", "fr", "uk"];

/** The real Moova TV logo (icon + MOOVA TV wordmark), never a text imitation. */
export function Brand({ light = false, className = "" }: { light?: boolean; className?: string }) {
  return (
    <span className={`moova-brand${light ? " is-light" : ""}${className ? ` ${className}` : ""}`}>
      <img src="/assets/moova-logo.png" alt="Moova TV" width="556" height="154" />
    </span>
  );
}

const LANG_NAMES: Record<Lang, string> = {
  pt: "Português",
  en: "English",
  es: "Español",
  de: "Deutsch",
  fr: "Français",
  uk: "Українська",
};

/**
 * Language picker: a button + listbox (WAI-ARIA listbox pattern) instead of a
 * native <select>. The panel unfolds with a clip-path reveal, options cascade
 * in, and a highlight pill glides to the hovered/focused option.
 */
function LanguageMenu({ language, onChange, label }: { language: Lang; onChange: (lang: Lang) => void; label: string }) {
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(() => Math.max(0, LANGS.indexOf(language)));
  const root = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = useRef<HTMLUListElement>(null);
  const listId = "moova-language-list";

  useEffect(() => {
    if (!open) return;
    const close = (e: PointerEvent) => {
      if (!root.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", close);
    list.current?.focus();
    return () => document.removeEventListener("pointerdown", close);
  }, [open]);

  const openMenu = () => {
    setActive(Math.max(0, LANGS.indexOf(language)));
    setOpen(true);
  };

  const choose = (lang: Lang) => {
    onChange(lang);
    setOpen(false);
    button.current?.focus();
  };

  const onButtonKey = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      openMenu();
    }
  };

  const onListKey = (e: React.KeyboardEvent) => {
    const last = LANGS.length - 1;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => (i >= last ? 0 : i + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => (i <= 0 ? last : i - 1)); }
    else if (e.key === "Home") { e.preventDefault(); setActive(0); }
    else if (e.key === "End") { e.preventDefault(); setActive(last); }
    else if (e.key === "Enter" || e.key === " ") { e.preventDefault(); choose(LANGS[active]); }
    else if (e.key === "Escape") { e.preventDefault(); setOpen(false); button.current?.focus(); }
    else if (e.key === "Tab") setOpen(false);
  };

  return (
    <div ref={root} className={`moova-language${open ? " is-open" : ""}`}>
      <button
        ref={button}
        type="button"
        className="moova-language-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        aria-label={`${label}: ${LANG_NAMES[language]}`}
        onClick={() => (open ? setOpen(false) : openMenu())}
        onKeyDown={onButtonKey}
      >
        <Globe2 size={14} aria-hidden="true" />
        <span className="moova-language-code">{language.toUpperCase()}</span>
        <ChevronDown size={11} aria-hidden="true" className="moova-language-chevron" />
      </button>
      <ul
        ref={list}
        id={listId}
        role="listbox"
        tabIndex={-1}
        aria-label={label}
        aria-activedescendant={open ? `moova-lang-${LANGS[active]}` : undefined}
        className="moova-language-panel"
        style={{ "--moova-lang-active": active } as React.CSSProperties}
        onKeyDown={onListKey}
        hidden={!open}
      >
        <li className="moova-language-pill" role="presentation" aria-hidden="true" />
        {LANGS.map((l, i) => (
          <li
            key={l}
            id={`moova-lang-${l}`}
            role="option"
            aria-selected={l === language}
            className={`moova-language-option${i === active ? " is-active" : ""}`}
            style={{ "--moova-lang-i": i } as React.CSSProperties}
            onPointerEnter={() => setActive(i)}
            onClick={() => choose(l)}
          >
            <span className="moova-language-option-code">{l.toUpperCase()}</span>
            <span className="moova-language-option-name" lang={l}>{LANG_NAMES[l]}</span>
            {l === language && <Check size={14} aria-hidden="true" className="moova-language-check" />}
          </li>
        ))}
      </ul>
    </div>
  );
}

function useChromeCopy() {
  const { language, setLanguage } = useLanguage();
  const c = getHomeCopy(language);
  const nav = (k: string) => t("nav", k, language);
  const lp = (k: string) => t("lp", k, language);
  const tr = (k: string) => tHome(k, language);
  return { language, setLanguage, c, nav, lp, tr };
}

/** Section anchor on the home; a route+hash link everywhere else. */
function SectionLink({ hash, children, onClick, className }: { hash: string; children: React.ReactNode; onClick?: () => void; className?: string }) {
  const { pathname } = useLocation();
  if (pathname === HOME) {
    return (
      <a href={`#${hash}`} onClick={onClick} className={className}>
        {children}
      </a>
    );
  }
  return (
    <Link to={`${HOME}#${hash}`} onClick={onClick} className={className}>
      {children}
    </Link>
  );
}

export function Header() {
  const { language, setLanguage, c, nav, lp } = useChromeCopy();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [compact, setCompact] = useState(false);
  const [section, setSection] = useState("home");
  const menuButton = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLElement>(null);
  const { pathname } = useLocation();

  useEffect(() => {
    const query = window.matchMedia('(max-width: 1080px)');
    const update = () => { setCompact(query.matches); setOpen(false); };
    update();
    query.addEventListener('change', update);
    return () => query.removeEventListener('change', update);
  }, []);

  useEffect(() => { setOpen(false); setExpanded(false); }, [pathname]);

  useEffect(() => {
    if (pathname !== HOME) return;
    const elements = ['overview', 'experience', 'companion', 'setup', 'pricing'].map(id => document.getElementById(id)).filter((el): el is HTMLElement => Boolean(el));
    const observer = new IntersectionObserver(entries => {
      const current = entries.find(entry => entry.isIntersecting);
      if (current) setSection(current.target.id === 'pricing' ? 'pricing' : ['experience', 'companion'].includes(current.target.id) ? 'features' : current.target.id === 'setup' ? 'activation' : 'home');
    }, { rootMargin: '-20% 0px -60% 0px' });
    elements.forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, [pathname]);

  useEffect(() => {
    if (!open && !expanded) return;
    const previousFocus = document.activeElement as HTMLElement | null;
    const previousOverflow = document.body.style.overflow;
    if (open && compact) {
      document.body.style.overflow = 'hidden';
      panel.current?.querySelector<HTMLButtonElement>('button')?.focus();
    }
    const close = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        setExpanded(false);
        if (!compact) panel.current?.querySelector<HTMLButtonElement>('button')?.focus();
      }
      if (event.key === 'Tab' && open && compact && panel.current) {
        const targets = Array.from(panel.current.querySelectorAll<HTMLElement>('a[href], button'));
        const first = targets[0];
        const last = targets[targets.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
      }
    };
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("keydown", close);
      document.body.style.overflow = previousOverflow;
      if (open && compact) previousFocus?.focus({ preventScroll: true });
    };
  }, [open, expanded, compact]);

  // Same destinations as the live header: home, features, pricing, activation, playlist, reseller.
  const links: { key: string; label: string; icon: LucideIcon; to?: string; hash?: string; end?: boolean }[] = [
    { key: "home", label: nav("home"), icon: Home, to: HOME, end: true },
    { key: "features", label: lp("menuFeatures"), icon: Sparkles, hash: "experience" },
    { key: "pricing", label: nav("pricing"), icon: CreditCard, to: "/pricing" },
    { key: "activation", label: nav("activation"), icon: KeyRound, to: "/activation" },
    { key: "playlist", label: nav("playlist"), icon: ListVideo, to: "/playlist" },
    { key: "reseller", label: nav("reseller"), icon: Users, to: "/reseller/register" },
  ];
  const navigate = () => { setOpen(false); setExpanded(false); };
  const renderLink = (l: (typeof links)[number], side = false) => {
    const Icon = l.icon;
    const content = side ? <><Icon size={21} strokeWidth={1.6} aria-hidden="true" /><span>{l.label}</span><ChevronRight className="moova-side-arrow" size={15} aria-hidden="true" /></> : l.label;
    return l.hash ? (
      <SectionLink key={l.key} hash={l.hash} onClick={navigate} className={pathname === HOME && section === l.key ? 'is-active' : undefined}>
        {content}
      </SectionLink>
    ) : (
      <NavLink key={l.key} to={l.to!} end={l.end} className={({ isActive }) => ((pathname === HOME && side ? section === l.key : isActive) ? "is-active" : undefined)} onClick={navigate}>
        {content}
      </NavLink>
    );
  };

  return <>
    <header className="moova-header">
      <div className="moova-nav moova-container">
        <Link to={HOME} aria-label="Moova TV" onClick={() => setOpen(false)}>
          <Brand />
        </Link>
        <nav className="moova-desktop-nav" aria-label={c.overview}>
          {links.map((l) => renderLink(l))}
        </nav>
        <div className="moova-nav-actions">
          <LanguageMenu language={language} onChange={setLanguage} label={nav("language")} />
          <Link className="moova-button moova-button-small" to="/activation">
            {lp("getStarted")}
            <ArrowRight size={13} />
          </Link>
          <button
            ref={menuButton}
            className="moova-menu-button"
            aria-label={open ? c.closeMenu : c.menu}
            aria-expanded={open}
            aria-controls="moova-side-panel"
            onClick={() => setOpen(!open)}
          >
            {open ? <X size={21} /> : <Menu size={21} />}
          </button>
        </div>
      </div>
    </header>
    {open && compact && <button className="moova-side-scrim" tabIndex={-1} onClick={() => setOpen(false)} aria-label={c.closeMenu} />}
    <aside ref={panel} id="moova-side-panel" className={`moova-side-panel${expanded ? ' is-expanded' : ''}${open ? ' is-open' : ''}`} role={compact && open ? 'dialog' : undefined} aria-modal={compact && open ? true : undefined} aria-label={c.menu}>
      <div className="moova-side-top">
        <button className="moova-side-toggle" aria-label={compact ? c.closeMenu : expanded ? c.closeMenu : c.menu} aria-expanded={compact ? open : expanded} onClick={() => compact ? setOpen(false) : setExpanded(!expanded)}>
          {compact ? <X size={21} /> : expanded ? <PanelLeftClose size={21} /> : <Menu size={21} />}<span>{c.overview}</span>
        </button>
      </div>
      <nav className="moova-side-nav" aria-label={c.overview}>{links.map(l => renderLink(l, true))}</nav>
      <div className="moova-side-bottom"><span className="moova-side-beacon" /><span>MOOVA TV</span><i /></div>
    </aside>
    <button className="moova-floating-menu" aria-label={open ? c.closeMenu : c.menu} aria-expanded={open} aria-controls="moova-side-panel" onClick={() => setOpen(!open)}>{open ? <X size={21} /> : <Menu size={21} />}<span>{c.overview}</span><i /></button>
  </>;
}

/** Footer: a light rail that draws in with the scroll, columns arriving in sequence, the brand mark drifting behind the grid. */
export function Footer() {
  const { c, lp, tr } = useChromeCopy();
  const ref = useRef<HTMLElement>(null);
  const reduced = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end end"] });
  const rail = useTransform(scrollYProgress, [0, .7], [0, 1]);
  const markY = useTransform(scrollYProgress, [0, 1], [90, -10]);
  const markOpacity = useTransform(scrollYProgress, [0, 1], [0, .14]);
  const column = (i: number) => ({
    initial: reduced ? false : { opacity: 0, y: 26 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, amount: 0.3 },
    transition: { duration: 0.7, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] as const },
  });
  return (
    <footer ref={ref} className="moova-footer">
      <div className="moova-footer-field" aria-hidden="true">
        <div className="moova-signal-floor"><div className="moova-signal-grid" /></div>
        <motion.img className="moova-footer-mark" src="/assets/moova-icon-logo.png" alt="" width="360" height="360" loading="lazy" decoding="async" style={reduced ? { opacity: .12 } : { y: markY, opacity: markOpacity }} />
      </div>
      <div className="moova-footer-rail" aria-hidden="true"><motion.span style={reduced ? undefined : { scaleX: rail }} /><i /></div>
      <div className="moova-container">
        <div className="moova-footer-top">
          <motion.div {...column(0)}>
            <Link to={HOME} aria-label="Moova TV">
              <Brand />
            </Link>
            <p>{c.footerLine}</p>
          </motion.div>
          <motion.div {...column(1)}>
            <span>{lp("footProduct")}</span>
            <SectionLink hash="experience">{c.experience}</SectionLink>
            <Link to="/pricing">{c.plans}</Link>
            <Link to="/playlist">{lp("footPlaylist")}</Link>
            <Link to="/activation">{lp("footActivation")}</Link>
          </motion.div>
          <motion.div {...column(2)}>
            <span>{lp("footReseller")}</span>
            <Link to="/reseller/register">{lp("footBecome")}</Link>
            <Link to="/auth/sign-in">{lp("footLogin")}</Link>
          </motion.div>
          <motion.div {...column(3)}>
            <span>{lp("footCompany")}</span>
            <a href="mailto:support@moova.tv">{lp("footContact")}</a>
            <Link to="/privacy">{lp("footPrivacy")}</Link>
            <Link to="/terms">{lp("footTerms")}</Link>
          </motion.div>
        </div>
        <p className="moova-footer-disclaimer">
          {tr("prDisclaimer")} {tr("fiTrademarks")}
        </p>
        <div className="moova-footer-bottom">
          <span>
            © {new Date().getFullYear()} Moova TV. {c.rights}
          </span>
          <span>Made for your moments.</span>
        </div>
      </div>
    </footer>
  );
}
